import { SetStateAction, useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import { getAuctionEventById } from "services";
import CustomErrorHandlingModal from "../CustomErrorHandlingModal";
import {
  bidderDataInitialise,
  useGetBasicDetails,
  getTeamMembersEmailDetails,
  handleBackendValidations,
  handleSubmitAllBiddersData,
  handleBidderDataSubmit,
  handlePANFieldValidations,
  useInitializeBetaForms,
  kycDetailsSwitchFields,
  focusFieldById,
} from "../bidderFormServices/getDetails";
import {
  BidderDocumentUploader,
  BidderFormFieldsHelper,
  BidderPDFViewer,
  NotificationModal,
  JointBidderSubmitButton,
  SubmitButtonComponent,
} from "../bidderFormServices/BidderFormFieldsHelper";
import { getJointFormFieldsConfig } from "./JointBidderFormConfig";
import { JBFormMode } from "./JBFormWrapper";

const isDigioPanFullVerificationEnabled =
  process.env.NEXT_PUBLIC_DIGIO_PAN_FULL_VERIFICATION_ENABLED === "true";

interface BidderFormProps {
  mode: JBFormMode;
  allBiddersData?: any;
  currentBidderData?: any;
  activeStep?: number;
  setActiveStep?: React.Dispatch<SetStateAction<number>>;
  bidderFormWrapperClassName?: string;
  setCurrentBidderData?: React.Dispatch<SetStateAction<Array<any>>>;
  totalSteps?: number;
  onNotFound?: () => void;
}
export const JointBidderForm = ({
  mode,
  currentBidderData,
  allBiddersData,
  activeStep = 0,
  setActiveStep,
  bidderFormWrapperClassName,
  setCurrentBidderData,
  totalSteps = 0,
  onNotFound,
}: BidderFormProps) => {
  const router = useRouter();
  const { for_event, bidders_count } = router.query;
  const { id } = router.query;
  const [event, setEvent] = useState<any>([]);
  const [renderKey, setRenderKey] = useState(0);
  const [bidderData, setBidderData] = useState<any>();
  const [membersEmail, setMembersEmail] = useState<any>([]);
  const [kycFiles, setKycFiles] = useState<any>([]);
  const [jointDeclarationFiles, setJointDeclarationFiles] = useState<any>([]);
  const [jointDeclarationRandomId, setJointDeclarationRandomId] =
    useState<any>();
  const [randomId, setRandomId] = useState<any>();
  const [isFetchingPan, setIsFetchingPan] = useState(false);
  const [disablePan, setDisablePan] = useState(false);
  const [isNameVerified, setIsNameVerified] = useState(false);
  type ErrorType = "validation" | "api";
  const [isErrorHandlingModalOpen, setIsErrorHandlingModalOpen] = useState<{
    open: boolean;
    type: ErrorType;
  }>({
    open: false,
    type: "validation",
  });
  const [errorHandlingModalData, setErrorHandlingModalData] =
    useState<any>(null);

  const [isOverwrite, setIsOverwrite] = useState(false);
  const [notificationPopup, setNotificationPopup] = useState("");

  const { banker, currentBranch, bank } = useGetBasicDetails();

  // V4 name-first flow: debounce ref for PAN verification trigger
  const panVerificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced function to trigger PAN verification when name >= 3 chars
  const triggerPanVerification = () => {
    if (panVerificationTimeoutRef.current) {
      clearTimeout(panVerificationTimeoutRef.current);
    }
    panVerificationTimeoutRef.current = setTimeout(() => {
      const nameValue = form?.getField?.("name");
      const panValue = form?.getField?.("pan");

      // Only trigger if name >= 3 AND PAN has value
      if (
        nameValue &&
        nameValue.length >= 3 &&
        panValue &&
        panValue.length >= 10 &&
        isDigioPanFullVerificationEnabled
      ) {
        handlePANFieldValidations(
          { target: { value: panValue } },
          form,
          setIsFetchingPan,
          setIsNameVerified,
        );
      }
    }, 500);
  };

  useEffect(() => {
    if (id && (mode === "joint-viewer" || mode === "joint-edit")) {
      (async () => {
        await bidderinit(id);
      })();
    }
    if (for_event) {
      (async () => {
        await init(for_event);
      })();
    }

    getTeamMembersEmailDetails(currentBranch, setMembersEmail);
  }, [for_event, id]);

  useEffect(() => {
    if (mode === "joint-creation") {
      if (!event.uuid) return;
      setRandomId(event.uuid);
      setJointDeclarationRandomId(event.uuid);
    }
  }, [event.uuid]);

  useEffect(() => {
    if (currentBidderData && mode === "joint-creation") {
      setBidderData(currentBidderData);
    }
  }, [activeStep, currentBidderData]);

  // In joint-edit mode, PAN is pre-filled and already verified — show the verified icon.
  useEffect(() => {
    if (mode === "joint-edit" && bidderData?.meta?.name) {
      setIsNameVerified(true);
    }
  }, [bidderData, mode]);

  const init = async (id: any) => {
    const data: any = await getAuctionEventById(id);
    setEvent(data);
  };

  const bidderinit = async (id: any) => {
    const data = await bidderDataInitialise(
      id,
      setBidderData,
      setKycFiles,
      setJointDeclarationFiles,
      setRenderKey,
    );
    if (!data) {
      onNotFound?.();
    }
  };

  console.log("🚀 ~ JointBidderForm ~ bidderData:", bidderData);
  const form = useInitializeBetaForms(
    bidderData,
    membersEmail,
    mode ?? "view",
    setIsNameVerified,
    "individual",
    activeStep,
  );

  const jointBidderFormFieldsConfig = getJointFormFieldsConfig(
    form,
    mode,
    setIsFetchingPan,
    setIsNameVerified,
    isNameVerified,
    disablePan,
    setDisablePan,
    setNotificationPopup,
    activeStep,
    triggerPanVerification,
  );

  // Keep initial_bid_price_confirmed in sync with the saved DB value in joint-edit mode.
  useEffect(() => {
    if (mode !== "joint-edit") return;
    const initialBid = form.value?.initial_bid_price;
    if (
      initialBid === "" ||
      initialBid === null ||
      typeof initialBid === "undefined"
    ) {
      form.setField("initial_bid_price_confirmed", false);
      form.removeError?.("initial_bid_price_confirmed");
      return;
    }
    const saved = bidderData?.meta?.initial_bid_price;
    const savedNum = typeof saved === "string" ? Number(saved) : saved;
    const shouldConfirm =
      savedNum !== undefined &&
      !isNaN(savedNum) &&
      Number(initialBid) === Number(savedNum);
    form.setField("initial_bid_price_confirmed", shouldConfirm);
    form.removeError?.("initial_bid_price_confirmed");
  }, [form.value?.initial_bid_price, bidderData, mode]);

  const saveCurrentBidderData = () => {
    if (
      setCurrentBidderData &&
      activeStep !== undefined &&
      activeStep >= 0 &&
      activeStep < Number(bidders_count || 0)
    ) {
      let currentFormData: any = {
        name: form.getField("name"),
        email: form.getField("email"),
        pan: form.getField("pan"),
        mobile_number: form.getField("mobile_number"),
        aadhar_number: form.getField("aadhar_number"),
        address: form.getField("address"),
        user_type: form.getField("user_type"),
        kyc_verified: form.getField("kyc_verified"),
        emd_amount_paid: form.getField("emd_amount_paid"),
        agreement_signed: form.getField("agreement_signed"),
        application_form_filled: form.getField("application_form_filled"),
      };

      if (activeStep === 0) {
        currentFormData = {
          ...currentFormData,
          initial_bid_price: form.getField("initial_bid_price"),
          initial_bid_price_confirmed: form.getField(
            "initial_bid_price_confirmed",
          ),
        };
      }

      setCurrentBidderData((prevData: any) => {
        const updatedData = [...(prevData || [])];
        updatedData[activeStep] = currentFormData;
        return updatedData;
      });
    }
  };

  const saveDataAfterValidation = async () => {
    if (setActiveStep) {
      saveCurrentBidderData();
      setIsNameVerified(false);
      setActiveStep(activeStep + 1);
      setIsOverwrite(false);
      setDisablePan(false);
      form.removeError("email");
      form.removeError("mobile_number");
    }
  };

  const handleValidationForJointParticipation = async () => {
    // Reserve price validation must run before form.validate() so that the reserve price
    // error is shown before the confirmation-checkbox error (Bug fix).
    if (activeStep === 0) {
      const initialBid = form.getField("initial_bid_price");
      if (
        initialBid !== "" &&
        initialBid !== null &&
        typeof initialBid !== "undefined"
      ) {
        const reservePrice =
          event?.reserve_price ?? event?.property_dump?.reserve_price;
        if (
          reservePrice !== undefined &&
          reservePrice !== null &&
          reservePrice !== ""
        ) {
          if (Number(initialBid) < Number(reservePrice)) {
            form.pushError({
              initial_bid_price: [
                `Initial Bid Price must be greater than or equal to the Reserve Price (₹${Number(
                  reservePrice,
                ).toLocaleString("en-IN")}).`,
              ],
            });
            try {
              focusFieldById("initial_bid_price");
            } catch (e) {
              /* ignore */
            }
            return;
          }
        }
      }
    }

    if (form.validate() && setActiveStep && (activeStep === 0 || activeStep)) {
      let isValid = await handleBackendValidations(
        form,
        event?.uuid,
        setIsErrorHandlingModalOpen,
        setErrorHandlingModalData,
      );

      if (!isValid) return;
      // Save current bidder data before moving to next step
      saveDataAfterValidation();
    }
  };

  const handleJointEdit = async () => {
    // Reserve price check before form.validate() — same fix as creation flow.
    const initialBid = form.getField("initial_bid_price");
    if (
      initialBid !== "" &&
      initialBid !== null &&
      typeof initialBid !== "undefined"
    ) {
      const reservePrice =
        event?.reserve_price ?? event?.property_dump?.reserve_price;
      if (
        reservePrice !== undefined &&
        reservePrice !== null &&
        reservePrice !== ""
      ) {
        if (Number(initialBid) < Number(reservePrice)) {
          form.pushError({
            initial_bid_price: [
              `Initial Bid Price must be greater than or equal to the Reserve Price (₹${Number(
                reservePrice,
              ).toLocaleString("en-IN")}).`,
            ],
          });
          try {
            focusFieldById("initial_bid_price");
          } catch (e) {
            /* ignore */
          }
          return;
        }
      }

      const confirmed = form.getField("initial_bid_price_confirmed");
      if (!confirmed) {
        form.pushError({
          initial_bid_price_confirmed: [
            "Please tick the checkbox below the offer price field to confirm the amount.",
          ],
        });
        try {
          focusFieldById("initial_bid_price_confirmed");
        } catch (e) {
          /* ignore */
        }
        return;
      }
    }

    await handleBidderDataSubmit(
      form,
      "edit",
      id,
      for_event,
      banker,
      bank,
      currentBranch,
      setIsErrorHandlingModalOpen,
      setErrorHandlingModalData,
      typeof id === "string" ? id : randomId,
      kycFiles,
      false,
      router,
    );
  };

  const handlePreviousStep = () => {
    if (setActiveStep && activeStep && activeStep > 0) {
      setActiveStep(activeStep - 1);
      saveCurrentBidderData();
      setDisablePan(true);
    }
  };

  const handleSubmitAllBidders = async () => {
    handleSubmitAllBiddersData(
      for_event,
      allBiddersData,
      setActiveStep,
      setErrorHandlingModalData,
      setIsErrorHandlingModalOpen,
      banker,
      bank,
      currentBranch,
      isOverwrite,
      activeStep,
      kycFiles,
      jointDeclarationFiles,
      randomId,
      jointDeclarationRandomId,
    );
  };

  const handleAddBidderAnyway = async () => {
    try {
      if (activeStep === 0 || activeStep < totalSteps - 1) {
        saveDataAfterValidation();
      } else if (activeStep && activeStep === totalSteps - 1) {
        await handleSubmitAllBidders();
      }
    } catch (error) {
      console.log("error:check here---->>>", error);
    }
  };

  return (
    <>
      <div
        className={` ${
          bidderFormWrapperClassName ??
          "lg:pl-9 lg:py-[41px] lg:pr-[70px] space-y-8 lg:space-y-7 lg:mr-[88px] mt-9 lg:mt-6 lg:bg-brand-bgLightGrey"
        }`}
      >
        {mode === "joint-edit" ? (
          <>
            {jointBidderFormFieldsConfig?.map((field) => (
              <BidderFormFieldsHelper
                key={field.id}
                {...field}
                mode={mode}
                isFetchingPan={isFetchingPan}
              />
            ))}

            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-y-6 mt-3 lg:mt-0  lg:gap-y-5 ">
              {kycDetailsSwitchFields?.map((item: any, index) => (
                <BidderFormFieldsHelper
                  key={index}
                  id={`asset-${index}`}
                  formFieldType="switch_field"
                  fieldLabel={item?.title}
                  switchFieldValue={
                    form.getField(item?.value) !== ""
                      ? form.getField(item?.value)
                      : false
                  }
                  setSwitchFieldValue={(checked: boolean) =>
                    form.setField(item?.value, checked)
                  }
                  disableConditions={false}
                  mode={mode}
                />
              ))}
            </div>

            <BidderDocumentUploader
              hideConditions={false}
              property_uuid={typeof id === "string" ? id : randomId}
              owner_service_type="kyc-documents"
              dataFiles={kycFiles}
              setDataFiles={setKycFiles}
              allowedMimeTypes={["application/pdf", "image/png"]}
              renderKey={renderKey}
              headingText="Upload KYC documents"
              disableConditions={false}
            />

            <SubmitButtonComponent
              mode={mode}
              handleAcceptAction={handleJointEdit}
              acceptButtonDisabled={false}
              hideConditions={false}
              buttonLabel="Update"
            />
          </>
        ) : (
          <>
            {activeStep + 1 === totalSteps ? (
              <>
                <BidderDocumentUploader
                  hideConditions={false}
                  property_uuid={
                    mode === "joint-creation" && typeof id === "string"
                      ? id
                      : randomId
                  }
                  owner_service_type="joint-declaration-documents"
                  dataFiles={jointDeclarationFiles}
                  setDataFiles={setJointDeclarationFiles}
                  allowedMimeTypes={["application/pdf", "image/png"]}
                  renderKey={renderKey}
                  headingText="Joint Declaration Form Upload"
                  disableConditions={false}
                />

                <BidderDocumentUploader
                  hideConditions={false}
                  property_uuid={
                    mode === "joint-creation" && typeof id === "string"
                      ? id
                      : randomId
                  }
                  owner_service_type="kyc-documents"
                  dataFiles={kycFiles}
                  setDataFiles={setKycFiles}
                  allowedMimeTypes={["application/pdf", "image/png"]}
                  renderKey={renderKey}
                  headingText="Upload KYC documents"
                  disableConditions={false}
                />
              </>
            ) : (
              <>
                {jointBidderFormFieldsConfig?.map((field) => (
                  <BidderFormFieldsHelper
                    key={field.id}
                    {...field}
                    mode={mode}
                  />
                ))}

                <div className="flex flex-col lg:grid lg:grid-cols-2 gap-y-6 mt-3 lg:mt-0  lg:gap-y-5 ">
                  {kycDetailsSwitchFields?.map((item: any, index) => (
                    <BidderFormFieldsHelper
                      key={index}
                      id={`asset-${index}`}
                      formFieldType="switch_field"
                      fieldLabel={item?.title}
                      switchFieldValue={
                        form.getField(item?.value) !== ""
                          ? form.getField(item?.value)
                          : false
                      }
                      setSwitchFieldValue={(checked: boolean) =>
                        form.setField(item?.value, checked)
                      }
                      disableConditions={mode === "joint-viewer"}
                      mode={mode}
                    />
                  ))}
                </div>

                <BidderPDFViewer
                  hideConditions={mode === "joint-creation"}
                  renderKey={renderKey}
                  dataFiles={kycFiles}
                  setDataFiles={setKycFiles}
                  allowedMimeTypes={["application/pdf"]}
                  property_uuid={typeof id === "string" ? id : randomId}
                  owner_service_type="kyc-documents"
                  headingText="View KYC documents"
                />

                <BidderPDFViewer
                  hideConditions={mode === "joint-creation"}
                  renderKey={renderKey}
                  dataFiles={jointDeclarationFiles}
                  setDataFiles={setJointDeclarationFiles}
                  allowedMimeTypes={["application/pdf"]}
                  property_uuid={typeof id === "string" ? id : randomId}
                  owner_service_type="joint-declaration"
                  headingText="View Joint Declaration Files"
                />
              </>
            )}
            <JointBidderSubmitButton
              mode="joint-creation"
              activeStep={activeStep}
              totalSteps={totalSteps}
              handlePreviousStep={handlePreviousStep}
              handleSubmitAllBidders={handleSubmitAllBidders}
              handleNextStep={handleValidationForJointParticipation}
              hideConditions={mode === "joint-viewer"}
            />
          </>
        )}
      </div>
      {notificationPopup !== "" && (
        <NotificationModal
          isOpen={notificationPopup}
          onClose={setNotificationPopup}
        />
      )}
      {isErrorHandlingModalOpen?.open && (
        <CustomErrorHandlingModal
          errorType={isErrorHandlingModalOpen?.type || "validation"}
          onClose={setIsErrorHandlingModalOpen}
          errorValue={errorHandlingModalData}
          setIsErrorHandlingModalOpen={setIsErrorHandlingModalOpen}
          setErrorHandlingModalData={setErrorHandlingModalData}
          errorHandlingModalData={errorHandlingModalData}
          handleAddBidderAnyway={handleAddBidderAnyway}
          setIsOverwrite={setIsOverwrite}
        />
      )}
    </>
  );
};

export default JointBidderForm;
